import { useEffect, useRef } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { ArrowRightSmall } from '@zoopla-eng/zds-components/icons';
import { Bulleted } from '@zoopla-eng/zds-components/lists/bulleted';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

import { titleStyle } from './ConfirmationStep.styles.css';
import { trackSegmentAndGAEvent } from '@zoopla/tracking';
import { StepProps } from '../../AuthModal.types';
import { useConfirmationStep } from './useConfirmationStep';
import { NextBestAction } from '../../../../graphql.generated';
import { Button, ButtonLinkRight } from '@zoopla-eng/zds-components/button';

export type ConfirmationStepProps = {
    title?: string;
    nextBestActionData: NextBestAction | undefined | null;
    onSkipClick: () => void;
} & Omit<StepProps, 'onStepCompleted'>;

export const ConfirmationStep = ({
    title: stepTitle,
    nextBestActionData,
    trackingData,
    onSkipClick
}: ConfirmationStepProps) => {
    const stepTitleRef = useRef<HTMLHeadingElement>(null);
    const { getContent, getTrackingData } =
        useConfirmationStep(nextBestActionData);

    const { title, url, copy, cta } = getContent();
    const label = getTrackingData();

    useEffect(() => {
        stepTitleRef.current?.focus();

        trackSegmentAndGAEvent(
            'Module Shown',
            {
                eventCategory: 'next_best_action',
                eventAction: 'module_shown',
                eventLabel: label
            },
            trackingData.segmentData.page
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLinkClick = () => {
        trackSegmentAndGAEvent(
            'Module Interaction',
            {
                eventCategory: 'next_best_action',
                eventAction: 'click',
                eventLabel: label
            },
            trackingData.segmentData.page
        );
    };

    const onClickedSkipButton = () => {
        trackSegmentAndGAEvent(
            'Module Interaction',
            {
                eventCategory: 'next_best_action',
                eventAction: 'skip',
                eventLabel: label
            },
            trackingData.segmentData.page
        );

        onSkipClick();
    };

    const textComponent = Array.isArray(copy) ? (
        <Bulleted
            items={copy.map((item) => (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>{item}</>
            ))}
        />
    ) : copy !== '' ? (
        <p>{copy}</p>
    ) : null;

    if (url === '') {
        // error state: skip to next step if next best action coming back from BE is invalid
        onSkipClick();
    }

    return (
        <>
            {stepTitle && (
                <Box pb="S400">
                    <h1
                        className={fontGroups.mug}
                        tabIndex={-1}
                        ref={stepTitleRef}
                    >
                        {stepTitle}
                    </h1>
                </Box>
            )}
            <Box pb="S400">
                <p className={`${fontGroups.ladle} ${titleStyle}`}>{title}</p>
            </Box>
            <Box pb="S400">{textComponent}</Box>
            <Box pb="S400" display="flex">
                <ButtonLinkRight
                    href={url}
                    onClick={onLinkClick}
                    Icon={ArrowRightSmall}
                    category="primary"
                >
                    {cta}
                </ButtonLinkRight>
            </Box>
            <Box pb="S400">
                <Button category="tertiary" onClick={onClickedSkipButton}>
                    Skip for now
                </Button>
            </Box>
        </>
    );
};
